// SPDX-License-Identifier: AGPL-3.0-or-later
import {z} from 'zod';

const id = z
	.string()
	.regex(/^[a-z][a-z0-9_]{0,63}$/, 'IDs start with a letter and use lowercase letters, numbers and underscores');
const field = z
	.string()
	.regex(/^[a-z][a-zA-Z0-9_]{0,63}$/, 'Parameter names start with a lowercase letter and use letters, numbers and _');
const endpoint = z
	.string()
	.url()
	.refine((v) => {
		const u = new URL(v);
		return u.protocol === 'https:' && !u.username && !u.password && !u.hash;
	}, 'Use an HTTPS URL without credentials or a fragment');
export const ConnectorParameterSchema = z
	.object({
		name: field,
		label: z.string().min(1).max(100),
		type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
		schema: z.record(z.string(), z.unknown()).optional(),
		required: z.boolean().default(false),
		choices: z.array(z.string().max(200)).max(100).optional(),
	})
	.strict();
export const ConnectorCapabilitySchema = z
	.object({
		id,
		label: z.string().min(1).max(160),
		description: z.string().max(1000).default(''),
		method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
		path: z
			.string()
			.min(1)
			.max(500)
			.refine(
				(p) =>
					p.startsWith('/') &&
					!p.startsWith('//') &&
					!/[?#\\\r\n]/.test(p) &&
					!p.split('/').some((s) => s === '.' || s === '..'),
				'Use an absolute API path; parameters use {name}',
			),
		effect: z.enum(['read', 'write']),
		parameters: z.array(ConnectorParameterSchema).max(30).default([]),
		scope: z.array(field).max(30).default([]),
		query: z.array(field).max(30).default([]),
		body: z.array(field).max(30).default([]),
	})
	.strict()
	.superRefine((c, ctx) => {
		for (const p of c.parameters) {
			if (p.type === 'object' || p.type === 'array') {
				if (!p.schema || p.schema.type !== p.type)
					ctx.addIssue({code: 'custom', message: `Provide a JSON schema for ${p.name}`});
				else if (JSON.stringify(p.schema).length > 16000 || JSON.stringify(p.schema).includes('"$ref"'))
					ctx.addIssue({code: 'custom', message: 'Parameter schemas must be bounded and inline'});
			}
		}
		for (const p of c.parameters) {
			if (p.choices?.length && p.type !== 'string')
				ctx.addIssue({code: 'custom', message: 'Choices apply to string parameters'});
			if ((p.type === 'object' || p.type === 'array') && (c.query.includes(p.name) || c.path.includes(`{${p.name}}`)))
				ctx.addIssue({code: 'custom', message: 'Object and array parameters belong in the JSON body'});
		}
		const names = new Set(c.parameters.map((p) => p.name));
		if (names.size !== c.parameters.length) ctx.addIssue({code: 'custom', message: 'Parameter names must be unique'});
		for (const name of [
			...c.scope,
			...c.query,
			...c.body,
			...Array.from(c.path.matchAll(/\{([a-z][a-zA-Z0-9_]*)\}/g), (m) => m[1]),
		])
			if (!names.has(name)) ctx.addIssue({code: 'custom', message: `Unknown parameter: ${name}`});
		if (c.effect === 'read' && c.method !== 'GET')
			ctx.addIssue({code: 'custom', message: 'Read capabilities must use GET'});
	});
export const AssistantConnectorDefinitionSchema = z
	.object({
		id,
		name: z.string().trim().min(1).max(100),
		description: z.string().max(1000).default(''),
		// GitHub is the one built-in connector; everything else is a staff-defined custom connector.
		provider: z.enum(['github', 'api']),
		base_url: endpoint,
		network: z.enum(['public', 'private']).default('public'),
		enabled: z.boolean().default(true),
		shared: z.boolean().default(false),
		auth: z.enum(['bearer', 'oauth2']),
		client_id: z.string().max(300).default(''),
		authorize_url: endpoint.optional(),
		token_url: endpoint.optional(),
		scopes: z.string().max(1000).default(''),
		capabilities: z.array(ConnectorCapabilitySchema).max(50).default([]),
	})
	.strict()
	.superRefine((d, ctx) => {
		if (d.network === 'private' && d.provider !== 'api')
			ctx.addIssue({code: 'custom', message: 'Private network access is available only for custom connectors'});
		if (d.auth === 'oauth2' && (!d.client_id || !d.authorize_url || !d.token_url))
			ctx.addIssue({code: 'custom', message: 'OAuth requires a client ID, authorization URL and token URL'});
		if (new Set(d.capabilities.map((c) => c.id)).size !== d.capabilities.length)
			ctx.addIssue({code: 'custom', message: 'Action IDs must be unique'});
	});
export type AssistantConnectorDefinition = z.infer<typeof AssistantConnectorDefinitionSchema>;

// Who may see and use a connector. Stored beside the definition (not in it) so changing access
// doesn't bump the connector version and send everyone's automations back for review.
const userId = z.string().regex(/^\d{1,20}$/, 'Choose a Konvo member');
export const ConnectorRightsSchema = z
	.object({
		read: z.boolean().default(false),
		create: z.boolean().default(false),
		update: z.boolean().default(false),
		delete: z.boolean().default(false),
	})
	.strict();
export const ConnectorAccessSchema = z
	.object({
		/** Anyone without a member row. New connectors start with nothing. */
		everyone: ConnectorRightsSchema.default({read: false, create: false, update: false, delete: false}),
		members: z
			.array(ConnectorRightsSchema.extend({user_id: userId}))
			.max(500)
			.default([]),
		/** Per-action override: only these members may use the action, whatever the grid says. */
		actions: z.record(id, z.array(userId).max(500)).default({}),
	})
	.strict()
	.superRefine((a, ctx) => {
		if (new Set(a.members.map((m) => m.user_id)).size !== a.members.length)
			ctx.addIssue({code: 'custom', message: 'Each member can only be listed once'});
	});
export type ConnectorRights = z.infer<typeof ConnectorRightsSchema>;
export type ConnectorAccess = z.infer<typeof ConnectorAccessSchema>;
export type ConnectorRight = keyof ConnectorRights;
export const NO_CONNECTOR_ACCESS: ConnectorAccess = {
	everyone: {read: false, create: false, update: false, delete: false},
	members: [],
	actions: {},
};
/** Each action counts as one grid column by its HTTP method. */
export function actionRight(method: ConnectorCapability['method']): ConnectorRight {
	return method === 'GET' ? 'read' : method === 'POST' ? 'create' : method === 'DELETE' ? 'delete' : 'update';
}
/** Staff always have full access; this answers for everyone else. */
export function canUseConnectorAction(access: ConnectorAccess, user: string, capability: ConnectorCapability) {
	const only = access.actions[capability.id];
	if (only) return only.includes(user);
	const rights = access.members.find((m) => m.user_id === user) ?? access.everyone;
	return rights[actionRight(capability.method)];
}
/** A member sees a connector when any right or action override includes them. */
export function canSeeConnector(access: ConnectorAccess, user: string) {
	const rights = access.members.find((m) => m.user_id === user) ?? access.everyone;
	return (
		rights.read ||
		rights.create ||
		rights.update ||
		rights.delete ||
		Object.values(access.actions).some((ids) => ids.includes(user))
	);
}
export type ConnectorCapability = z.infer<typeof ConnectorCapabilitySchema>;
export type ConnectorParameter = z.infer<typeof ConnectorParameterSchema>;
/** A Konvo member as shown in the access grid; `avatar` is the user's avatar hash. */
export interface ConnectorMemberView {
	user_id: string;
	name: string;
	username: string | null;
	avatar: string | null;
}
export interface AssistantConnectorView {
	/** For members, capabilities are narrowed to the actions they may use. */
	definition: AssistantConnectorDefinition;
	version: number;
	/** Staff only: the full access grid, with member names resolved. */
	access?: ConnectorAccess;
	access_members?: Array<ConnectorMemberView>;
	connected: boolean;
	credential_saved: boolean;
	client_secret_saved: boolean;
	account: string | null;
	health: 'ready' | 'not_connected' | 'reconnect' | 'disabled';
	checked_at: string | null;
	last_error: string | null;
	affected_flows?: Array<{id: string; name: string; reason: string}>;
}
